import { addHours } from "date-fns";
import { useDataProvider, useVersion } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { stringify } from "../pages/bookings/util";
import { Booking } from "../types";
import { parseToClientParts } from "../utils/date";

export interface BookingEvent {
  title: string;
  status: string;
  date: string;
  start: Date;
  end: Date;
  resource: Booking;
}

/** Collapse rapid prev/next clicks into one request. */
const RANGE_DEBOUNCE_MS = 350;

/**
 * Booking dates arrive as Asia/Singapore wall time — naive strings stay
 * literal, marker-bearing strings (Z / offset) are converted to SGT via
 * parseToClientParts before constructing the local Date the calendar grid
 * renders from.
 */
const parseBookingDate = (raw: string): Date => {
  const parts = parseToClientParts(raw);
  if (!parts) {
    return new Date(raw);
  }
  const [yyyy, mm, dd] = parts.date.split("-").map((v) => parseInt(v, 10));
  const [hh, mi] = parts.time.split(":").map((v) => parseInt(v, 10));
  return new Date(yyyy, mm - 1, dd, hh, mi, 0);
};

const toEvent = (booking: Booking): BookingEvent => {
  const start = parseBookingDate(String((booking as any).date ?? ""));
  return {
    title: stringify(booking),
    status: booking.status,
    date: booking.date,
    start,
    end: addHours(start, booking.is_eot ? 24 : 3),
    resource: booking,
  };
};

/**
 * Fetch the bookings falling in a "YYYY-MM-DD" range and map them to calendar events.
 * `total` counts bookings whose literal start date lands inside the range.
 *
 * Debounces range changes, ignores stale responses, and refetches when the
 * React-Admin topbar refresh increments `useVersion()`.
 */
export const useBookingEvents = (dateFrom?: string, dateTo?: string) => {
  const dataProvider = useDataProvider();
  const version = useVersion();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [events, setEvents] = useState<BookingEvent[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const prevRangeRef = useRef<{ from?: string; to?: string }>({});

  useEffect(() => {
    if (!dateFrom || !dateTo) {
      return;
    }

    const isInitialRange =
      prevRangeRef.current.from === undefined &&
      prevRangeRef.current.to === undefined;
    const rangeChanged =
      prevRangeRef.current.from !== dateFrom ||
      prevRangeRef.current.to !== dateTo;
    prevRangeRef.current = { from: dateFrom, to: dateTo };

    // Debounce only when the visible range changes (rapid prev/next).
    // First load and topbar refresh (version bump, same range) fetch immediately.
    const delay =
      isInitialRange || !rangeChanged ? 0 : RANGE_DEBOUNCE_MS;

    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(() => {
      dataProvider
        .getList("bookings", {
          pagination: { page: 1, perPage: 10000 },
          sort: { field: "date", order: "asc" },
          filter: { date_from: dateFrom, date_to: dateTo },
        })
        .then(({ data }: { data: any }) => {
          if (cancelled) {
            return;
          }
          setBookings(data as Booking[]);
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            console.error("Error fetching data:", error);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, version]);

  useEffect(() => {
    setEvents(bookings.map(toEvent));
  }, [bookings]);

  useEffect(() => {
    if (!dateFrom || !dateTo) {
      return;
    }

    setTotal(
      bookings.filter((booking) => {
        const raw = String((booking as any).date ?? "");
        const parts = parseToClientParts(raw);
        const literal = parts ? parts.date : raw.slice(0, 10);
        return literal >= dateFrom && literal <= dateTo;
      }).length,
    );
  }, [bookings, dateFrom, dateTo]);

  return { bookings, events, total, loading };
};

export default useBookingEvents;
